// ── certificate-issue-section: SA/Admin issue affordance (FR-CERT) ──────────
// POST /certificates issue (issue.certificates = SA/Admin) + a generated UUID
// Idempotency-Key (in the hook). Toasts ONLY after the backend confirms. The
// issued certificate's rendered document is PENDING (artifactRef null) — the
// success copy states the certificate is recorded and the document will follow;
// it NEVER implies a downloadable file exists. Rendered ONLY for SA/Admin.

import { Alert, Button, Input, Module, ModuleBody, ModuleHead, useToast } from "@vjal/ui";
import { useState } from "react";
import { Award } from "lucide-react";

import { useCertificateIssueMutation } from "../../hooks/useCoachingCertificates";
import { describeReportWriteError } from "../reports/reports-write";
import { hasCertificateArtifact } from "./certificates-helpers";

export function CertificateIssueSection() {
  const toast = useToast();
  const issue = useCertificateIssueMutation();
  const [participantId, setParticipantId] = useState("");
  const [cohortId, setCohortId] = useState("");

  const participant = Number(participantId);
  const cohort = Number(cohortId);
  const valid =
    Number.isInteger(participant) && participant > 0 && Number.isInteger(cohort) && cohort > 0;
  const writeError = describeReportWriteError(issue.error);
  const issued = issue.data?.certificate ?? null;

  function submit() {
    if (!valid) return;
    issue.reset();
    issue.mutate(
      { participantId: participant, cohortId: cohort },
      { onSuccess: () => toast.success("Certificate issued") }
    );
  }

  return (
    <Module span={12}>
      <ModuleHead title="Issue a certificate" subtitle="Super-admin / admin only. The rendered document follows separately." />
      <ModuleBody className="space-y-3" data-testid="certificate-issue">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Participant ID"
            type="number"
            min={1}
            value={participantId}
            onChange={(event) => setParticipantId(event.target.value)}
            aria-label="Participant ID"
          />
          <Input
            label="Cohort ID"
            type="number"
            min={1}
            value={cohortId}
            onChange={(event) => setCohortId(event.target.value)}
            aria-label="Cohort ID"
          />
        </div>
        <Button
          type="button"
          variant="primary"
          leftIcon={<Award size={16} />}
          disabled={!valid}
          loading={issue.isPending}
          onClick={submit}
          aria-label="Issue certificate"
        >
          Issue certificate
        </Button>

        {issued ? (
          <Alert variant="info">
            Certificate recorded (status: {issued.status}).{" "}
            {hasCertificateArtifact(issued)
              ? "The document is available in the list below."
              : "The certificate document is still pending — it will appear once it's generated. No download is available yet."}
          </Alert>
        ) : null}
        {writeError ? <Alert variant={writeError.tone}>{writeError.message}</Alert> : null}
      </ModuleBody>
    </Module>
  );
}
